import React, {useState} from 'react';
import Selector from "components/Form/Selector";
import {Item} from "components/Form/Selector/Selector";
import {Frequency as RRuleFrequency, RRule} from "rrule";
import {toInt} from "services/number";
import {DateTime} from "luxon";

// Types ----

enum Frequency {NEVER, MINUTELY, HOURLY, DAILY, WEEKLY, MONTHLY, YEARLY}

enum Stop {NEVER, ON, AFTER}

type RecurrenceBuilderProps = {
  rule?: RRule | null
  onChange: (ruleString: string | null) => any
}

// Helpers ----

function frequencyFromRule(rule?: RRule | null): Frequency {
  const frequencies: Record<RRuleFrequency, Frequency> = {
    [RRuleFrequency.YEARLY]: Frequency.YEARLY,
    [RRuleFrequency.MONTHLY]: Frequency.MONTHLY,
    [RRuleFrequency.WEEKLY]: Frequency.WEEKLY,
    [RRuleFrequency.DAILY]: Frequency.DAILY,
    [RRuleFrequency.HOURLY]: Frequency.HOURLY,
    [RRuleFrequency.MINUTELY]: Frequency.MINUTELY,
    [RRuleFrequency.SECONDLY]: Frequency.NEVER,
  }

  const rruleFrequency = rule?.options?.freq;

  return rruleFrequency ? (frequencies[rruleFrequency]) : Frequency.NEVER;
}

function stopFromRule(rule?: RRule | null): Stop {
  switch (true) {
    case !!(rule?.options?.until):
      return Stop.ON
    case !!(rule?.options?.count):
      return Stop.AFTER
    default:
      return Stop.NEVER
  }
}

function stopDateFromRule(rule?: RRule | null): string {
  const date = rule?.options?.until ? DateTime.fromJSDate(rule.options.until) : DateTime.now()
  return date.toFormat("yyyy-LL-dd")
}

function stopCountFromRule(rule?: RRule | null): number {
  return typeof rule?.options?.count === 'number' ? rule?.options.count : 0
}

function compileRuleString(frequency: Frequency, stop: Stop, stopDate?: Date | null, stopCount?: number | null): string | null {
  if (frequency === Frequency.NEVER) {
    return null
  }

  const frequencyMap: Record<Exclude<Frequency, Frequency.NEVER>, RRuleFrequency> = {
    [Frequency.YEARLY]: RRuleFrequency.YEARLY,
    [Frequency.MONTHLY]: RRuleFrequency.MONTHLY,
    [Frequency.WEEKLY]: RRuleFrequency.WEEKLY,
    [Frequency.DAILY]: RRuleFrequency.DAILY,
    [Frequency.MINUTELY]: RRuleFrequency.MINUTELY,
    [Frequency.HOURLY]: RRuleFrequency.HOURLY,
  }

  const rule = new RRule({
    freq: frequencyMap[frequency],
    interval: 1,
    until: (stop === Stop.ON && stopDate) || null,
    count: (stop === Stop.AFTER && stopCount) || null
  })

  return rule.toString()
}

const frequencyOptions: Array<Item> = [
  {id: Frequency.NEVER, name: 'Never'},
  {id: Frequency.MINUTELY, name: 'Every Minute'},
  {id: Frequency.HOURLY, name: 'Hourly'},
  {id: Frequency.DAILY, name: 'Daily'},
  {id: Frequency.WEEKLY, name: 'Weekly'},
  {id: Frequency.MONTHLY, name: 'Monthly'},
  {id: Frequency.YEARLY, name: 'Yearly'},
]

const stopOptions: Array<Item> = [
  {id: Stop.NEVER, name: 'Never'},
  {id: Stop.ON, name: 'On'},
  {id: Stop.AFTER, name: 'After'},
]

// Component ----

export default function RecurrenceBuilder({rule, onChange}: RecurrenceBuilderProps) {
  const [frequency, setFrequency] = useState<Frequency>(frequencyFromRule(rule));
  const [stop, setStop] = useState<Stop>(stopFromRule(rule));
  const [stopDate, setStopDate] = useState<string>(stopDateFromRule(rule));
  const [stopCount, setStopCount] = useState<number>(stopCountFromRule(rule));

  function handleChangeFrequency(newFrequency: Frequency) {
    setFrequency(newFrequency)
    setStop(Stop.NEVER)
    setStopDate(DateTime.now().toFormat("yyyy-LL-dd"))
    setStopCount(0)

    onChange(compileRuleString(newFrequency, Stop.NEVER))
  }

  function handleChangeStop(newStop: Stop) {
    setStop(newStop)
    setStopDate(DateTime.now().toFormat("yyyy-LL-dd"))
    setStopCount(newStop === Stop.AFTER ? 1 : 0)

    onChange(compileRuleString(frequency, newStop))
  }

  function handleChangeStopDate(newDate: string) {
    setStopDate(newDate)

    onChange(compileRuleString(frequency, stop, new Date(newDate)))
  }

  function handleChangeStopCount(newCount: number) {
    setStopCount(newCount)

    onChange(compileRuleString(frequency, stop, null, newCount))
  }

  return (
    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">

      <div className="sm:col-span-3">
        <Selector
          label="Repeats"
          items={frequencyOptions}
          selectedId={frequency}
          onChange={item => handleChangeFrequency(item.id as Frequency)}
        />
      </div>

      {frequency !== Frequency.NEVER &&
        <>
          <div className="sm:col-span-2">
            <Selector
              label="Stops"
              items={stopOptions}
              selectedId={stop}
              onChange={item => handleChangeStop(item.id as Stop)}
            />
          </div>

          {stop === Stop.ON &&
            <div className="sm:col-span-3">
              <input
                type="date"
                value={stopDate}
                onChange={event => handleChangeStopDate(event.currentTarget.value)}
                className="sm:mt-6 shadow-sm focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          }

          {stop === Stop.AFTER &&
            <div className="sm:col-span-3 xl:col-span-2">
              <div className="sm:mt-6 relative rounded-md shadow-sm">
                <input
                  type="number"
                  step={1}
                  min={1}
                  value={stopCount}
                  onChange={event => handleChangeStopCount(toInt(event.currentTarget.value) || 1)}
                  className="focus:ring-brand focus:border-brand block w-full sm:text-sm border-gray-300 rounded-md"
                />

                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">
                      occurrences
                  </span>
                </div>
              </div>
            </div>
          }
        </>
      }

    </div>
  );
}